import React, { useEffect, useMemo, useState } from "react"
import { useQuery } from "react-query"
import { AxiosError } from "axios"
import moment from "moment"
import { usePersonnelDetailsContext } from "../PersonnelDetails"
import { getPersonnelDeclarationsRequest } from "../../core/_requests"
import { IPersonnelDeclarationMensuelleModel } from "@common-models/*"
import MySimpleSpinner from "@common-utils/MySimpleSpinner"
import MyAlert from "@common-utils/MyAlert"
import MyModal from "@common-utils/MyModal"
import MyTooltip from "@common-utils/MyTooltip"
import PersonnelDeclarationEtatBadge from "./_PersonnelDeclarationEtatBadge"
import PersonnelDeclarationDetails from "../../declaration/details/_PersonnelDeclarationDetails"
import PersonnelDeclarationSuivi from "../../declaration/_PersonnelDeclarationSuivi"

const REACT_QUERY_KEY_GET_PERSONNEL_DECLARATIONS = "REACT_QUERY_KEY_GET_PERSONNEL_DECLARATIONS"

const capitalize = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s)

const PersonnelDeclarations = () => {
     const context = usePersonnelDetailsContext()
     const personnel = context.detailsQuery.data?.personnel
     const personnelId = personnel?.id as number

     // Déclaration ouverte dans le modal de détails (mêmes onglets Arrêts/DDC/CRA/NDF/TR que le listing)
     const [declarationInModal, setDeclarationInModal] = useState<IPersonnelDeclarationMensuelleModel | null>(null)
     // Déclaration ouverte dans le modal d'historique du suivi (clic sur l'état)
     const [suiviInModal, setSuiviInModal] = useState<IPersonnelDeclarationMensuelleModel | null>(null)
     // Année sélectionnée (onglets)
     const [selectedYear, setSelectedYear] = useState<number | null>(null)

     const query = useQuery<IPersonnelDeclarationMensuelleModel[], AxiosError>([REACT_QUERY_KEY_GET_PERSONNEL_DECLARATIONS, personnelId], () =>
          getPersonnelDeclarationsRequest(personnelId, {}).then(r => r.data)
     )

     // Regroupement par année (les données arrivent déjà triées par mois décroissant)
     const declarationsByYear = useMemo(() => {
          const map = new Map<number, IPersonnelDeclarationMensuelleModel[]>()
          ;(query.data ?? []).forEach(d => {
               const year = moment(d.mois).year()
               if (!map.has(year)) map.set(year, [])
               map.get(year)!.push(d)
          })
          return map
     }, [query.data])

     const years = useMemo(() => Array.from(declarationsByYear.keys()).sort((a, b) => b - a), [declarationsByYear])

     // Sélectionne l'année la plus récente par défaut
     useEffect(() => {
          if (selectedYear === null && years.length > 0) setSelectedYear(years[0])
     }, [years, selectedYear])

     // Le détail (onglets) charge ses données via declaration.id et declaration.personnel.id : on injecte
     // le personnel de la fiche (absent du payload de la liste).
     function openDeclaration(declaration: IPersonnelDeclarationMensuelleModel) {
          setDeclarationInModal({ ...declaration, personnel })
     }

     function openSuivi(declaration: IPersonnelDeclarationMensuelleModel) {
          setSuiviInModal({ ...declaration, personnel })
     }

     if (query.isFetching) {
          return (
               <div className={"text-center p-5"}>
                    <MySimpleSpinner size={"xl"} />
               </div>
          )
     }

     if (query.isError) {
          return (
               <MyAlert type={"danger"} classNames={"mb-0"}>
                    <>
                         <span className={"me-2"}>{query.error?.response?.data?.detail ?? "Une erreur est survenue"}</span>
                         <button className={"btn btn-sm btn-danger"} onClick={() => query.refetch()}>
                              Recharger
                         </button>
                    </>
               </MyAlert>
          )
     }

     if (!query.data || query.data.length === 0) {
          return (
               <MyAlert type={"primary"} classNames={"mb-0"}>
                    Aucune déclaration d'activité à afficher
               </MyAlert>
          )
     }

     const currentYearDeclarations = selectedYear !== null ? declarationsByYear.get(selectedYear) ?? [] : []

     return (
          <>
               {/* Onglets par année */}
               <div className={"d-flex flex-wrap gap-2 mb-6 border-bottom pb-4"}>
                    {years.map(year => (
                         <button
                              key={year}
                              type={"button"}
                              onClick={() => setSelectedYear(year)}
                              className={`btn btn-sm fw-bold ${selectedYear === year ? "btn-primary" : "btn-light-primary"}`}
                         >
                              {year}
                         </button>
                    ))}
               </div>

               {/* Mois de l'année sélectionnée */}
               <div className={"d-flex flex-column"}>
                    {currentYearDeclarations.map(declaration => (
                         <div
                              key={declaration.id}
                              onClick={() => openDeclaration(declaration)}
                              className={"d-flex align-items-center justify-content-between flex-wrap gap-3 py-3 px-4 rounded cursor-pointer bg-hover-light-primary border-bottom border-gray-200"}
                         >
                              <div className={"d-flex align-items-center"}>
                                   <span className={"symbol symbol-40px me-4"}>
                                        <span className={"symbol-label bg-light-primary"}>
                                             <i className={"fas fa-calendar-day fs-4 text-primary"} />
                                        </span>
                                   </span>
                                   <span className={"fw-bold text-gray-800 fs-6"}>{capitalize(moment(declaration.mois).format("MMMM YYYY"))}</span>
                              </div>
                              <div className={"d-flex align-items-center gap-4"}>
                                   <MyTooltip title={"Voir l'historique du suivi"}>
                                        <div className={"cursor-pointer"} onClick={e => { e.stopPropagation(); openSuivi(declaration) }}>
                                             <PersonnelDeclarationEtatBadge type={declaration.etat?.type} />
                                        </div>
                                   </MyTooltip>
                                   <MyTooltip title={"Consulter la déclaration"}>
                                        <i className={"fas fa-eye fs-3 text-gray-500"} />
                                   </MyTooltip>
                              </div>
                         </div>
                    ))}
               </div>

               {/* Modal de détails — mêmes onglets que le listing (Arrêts, DDC, CRA, NDF, TR) */}
               {declarationInModal && (
                    <MyModal
                         title={<span>Déclaration de {personnel?.prenomNom}</span>}
                         show={true}
                         fullscreen
                         onCloseCallback={() => setDeclarationInModal(null)}
                    >
                         <PersonnelDeclarationDetails
                              declaration={declarationInModal}
                              handleValidationCallback={() => query.refetch()}
                              handleDemandeRectificationCallback={() => query.refetch()}
                              handleValidationDemandeRegulCallback={() => query.refetch()}
                              handleRejetDemandeRegulCallback={() => query.refetch()}
                         />
                    </MyModal>
               )}

               {/* Modal historique du suivi — comme un clic sur l'état dans le listing */}
               {suiviInModal && (
                    <MyModal title={<span>Suivi de la déclaration de {personnel?.prenomNom}</span>} show={true} onCloseCallback={() => setSuiviInModal(null)}>
                         <PersonnelDeclarationSuivi declaration={suiviInModal} />
                    </MyModal>
               )}
          </>
     )
}

export default PersonnelDeclarations
